<template>
    <div class="fitness-settings">
        <div class="fitness-settings__form form">

            <!-- fitness goal -->
            <div class="form__title h5" v-translate>fitness_goal</div>
            <div class="form__text text"><translate>choose_goal_intro</translate>:</div>

            <div class="form__row form__row--multiple" v-for="(row, index) in chunkedGoals" :key="index" v-if="goals">
                <div class="form__item form__item--half" v-for="goal in row" :key="goal.id">
                    <div class="form__field form__field--radio">
                        <input type="radio" :id="goal.id" name="fitness-goal" :value="goal.id" v-model="formData.goalId">
                        <label :for="goal.id">
                            <span class="h6">{{goal.name}}</span>
                        </label>
                    </div>
                </div>
            </div>


            <!-- interests -->
            <div class="form__title h5" v-translate>favorite_activities</div>
            <div class="form__text text"><translate>activities_intro</translate>:</div>

            <div class="fitness-settings__interests" v-if="interests">
                <div class="fitness-settings__interests__item" :class="{'active' : interest.active}" v-for="interest in interests" :key="interest.id" @click="toggleInterest(interest)">
                    {{interest.name}}
                </div>
            </div>
            <div class="form__error" v-if="errors.indexOf('interests') >= 0" v-translate>at_least_two_interests</div>


            <!-- workout preferences -->
            <div class="form__title h5" v-translate>workout_preferences</div>
            <div class="form__text text" v-translate>workout_preferences_intro</div>

            <div class="form__row">
                <div class="form__item">
                    <div class="form__slider">
                        <VueSlider v-model="formData.preferences.workoutInterval" :min="1" :max="7" :tooltip="'always'" :dotSize="30"></VueSlider>
                    </div>
                    <div class="form__item__subtitle h6" v-translate>times_a_week</div>
                </div>
            </div>
            <div class="form__row">
                <div class="form__item">
                    <div class="form__slider">
                        <VueSlider v-model="formData.preferences.workoutTimeMinutes" :min="0" :max="24 * 60" :interval="15" :tooltipFormatter="formatTime" :tooltip="'always'" :dotSize="30"></VueSlider>
                    </div>
                    <div class="form__item__subtitle h6" v-translate>time</div>
                </div>
            </div>
            <div class="form__row form__row--space-below-large">
                <div class="form__item">
                    <div class="form__slider">
                        <VueSlider v-model="formData.preferences.workoutDuration" :min="15 * 60" :max="60 * 60" :interval="5 * 60" :tooltipFormatter="formatDuration" :tooltip="'always'" :dotSize="30"></VueSlider>
                    </div>
                    <div class="form__item__subtitle h6" v-translate>duration</div>
                </div>
            </div>


            <!-- button -->
            <div class="form__row">
                <div class="form__item">
                    <puma-button @click="submitProfileUpdate" :loading="submitButtonLoading" :disabled="!isFormValid"><translate>save_preferences</translate></puma-button>
                    <div class="form__error" v-if="submitErrors.length > 0">
                        <div v-for="error in submitErrors" :key="error">{{error}}</div>
                    </div>
                    <div class="form__error form__error--success" v-if="submitSuccess.length > 0">
                        <div v-for="success in submitSuccess" :key="success">{{success}}</div>
                    </div> 
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as api from '../api'
import { mapGetters } from "vuex"
import { storeUser } from '../utils/authentication';
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'

import {addTrackingObject} from "@/utils/tracking"

export default {
    name: "FitnessSettings",
    components: {
        VueSlider
    },
    data() {
        return {
            errors: [],
            submitErrors: [],
            submitSuccess: [],
            submitButtonLoading: false,
            goals: false,
            interests: false,
            formData: {
                goalId: '',
                interests: [],
                interestsIds: [],
                preferences: {
                    workoutTimeMinutes: 0,
                    workoutDuration: 0, // 15m - 1h
                    workoutInterval: 0, // 1-7
                    workoutTimeOfDay: '', // 00:00 - 24:00
                }
            }
        }
    },
    computed: {
        ...mapGetters(['user']),
        chunkedGoals() {
            const perChunk = 2;
            if(!this.goals) return []

            return this.goals.reduce((all,one,i) => {
                const ch = Math.floor(i/perChunk); 
                all[ch] = [].concat((all[ch]||[]),one); 
                return all
            }, [])
        },
        isFormValid() {
            return this.errors.length === 0
        },
    },
    mounted() {
        this.fetchGoals()
        this.fetchInterests()
        this.prepareFormData()

        addTrackingObject({subSection: "profile", screenName: "settingsWorkouts"}, {type: "profile", value: "web_userprofile_fitness_settings"});
    },
    methods: {
        prepareFormData() {
            const splitWorkoutTime = this.user.preferences.workoutTimeOfDay.split(':')
            this.formData.goalId = this.user.goal.id
            this.formData.interests = this.user.interests
            this.formData.preferences.workoutInterval = this.user.preferences.workoutInterval
            this.formData.preferences.workoutTimeMinutes = parseInt(splitWorkoutTime[0]) * 60 + parseInt(splitWorkoutTime[1])
            this.formData.preferences.workoutDuration = this.user.preferences.workoutDuration
        },
        toggleInterest(interest) {
            interest.active = !interest.active

            this.formData.interests = this.interests.filter(i => { return i.active === true })


            if(this.formData.interests.length < 2) {
                this.errors.push('interests')
            } else {
                this.errors = this.errors.filter(error => {return error !== 'interests'})
            }   
        },
        formatTime(value) {
            const hours = Math.floor(value / 60)
            const minutes = value % 60
            return `${this.padNumber(hours)}:${this.padNumber(minutes)}`
        },
        padNumber(number) {
            if(number < 10) {
                return `0${number}`
            }
            return number
        },
        formatDuration(value) {
            const minutes = value / 60
            return minutes < 60 ? `${minutes}min` : `1h`; 
        },
        submitProfileUpdate() {
            this.submitErrors = []
            this.submitSuccess = []
            
            if(this.isFormValid) {
                this.updateProfile()
            }
        },
        async updateProfile() {
            let data = {
                user: {...this.formData}
            }

            const workoutTimeHours = Math.floor(data.user.preferences.workoutTimeMinutes / 60)
            const workoutTimeMinutes = data.user.preferences.workoutTimeMinutes % 60
            data.user.preferences.workoutTimeOfDay = `${this.padNumber(workoutTimeHours)}:${this.padNumber(workoutTimeMinutes)}`

            data.user.interests.forEach(interest => {
                data.user.interestsIds.push(interest.id)
            })

            this.submitButtonLoading = true;
            try {
                const response = await api.users.updateProfile(data);
                const responseData = response.data.data;
                storeUser(responseData);

                this.submitSuccess.push('Settings have been successfully updated.')
            } catch (error) {
                console.error(error);
                if(error.response.status === 422) {
                    this.submitErrors.push(this.$gettext('form_error_cant_update_user'))
                }
            } finally {
                this.submitButtonLoading = false;
            }
        },
        async fetchGoals() {
            try {
                const response = await api.goals();
                this.goals = response.data.data                
            } catch (error) {
                console.error(error);
            }
        },
        async fetchInterests() {
            try {
                const response = await api.interests();
                this.interests = response.data.data
                this.interests.forEach(interest => {
                    const isActive = this.user.interests.filter(i => { return i.id === interest.id}).length !== 0
                    this.$set(interest, 'active', isActive) 
                })
            } catch (error) {
                console.error(error);
            }
        },

    }
}
</script>

<style lang="scss" scoped>
.fitness-settings {
    $block: &;

    .form {
        &__text {
            max-width: 700px;
        }

        &__row {
            max-width: 600px;
        }
    }


    &__interests {
        display: flex;
        flex-wrap: wrap;
        max-width: 800px;

        &__item {
            display: flex;
            align-items: center;
            height: 36px;
            border-radius: 500px;
            border: 1px solid $puma-color-neutral-20;
            padding: 0 16px;
            margin: 0 10px 15px 0;
            transition: all 200ms $ease-out-quad;
            cursor: pointer;

            &.active {
                background: black;
                color: white;
                border-color: black;
            }
        }
    }
}
</style>